import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isIOS, isSafari } from 'react-device-detect';

import { Close } from '@material-ui/icons';
import { Dialog, Divider, IconButton, List, ListItem, ListItemText, Menu, makeStyles } from '@material-ui/core';

import { setShowChatWindow, setShowShoppingCart } from '../../../actions/playerActions';
import { setOpenSettingMenu, setShowTranscriptionsModule, setSubtitleLanguage } from '../../../actions/configurationActions';
import { joinClassNames } from '../../../util/util';
import { MenuUtils } from './MenuUtils';

interface QualitySettingMenuProps {
	playerConfigs?: any;
	isMobile?: boolean;
	sideWindowOpen?: boolean;
	className?: string;
	queryParams?: any;
	onLanguageChange?: (_type: 'audio' | 'subtitle', _code: any) => void;
}

const useListCustomStyle = makeStyles(() => ({
	listItem: {
		minWidth: '190px',
		color: '#fff',
	},
	listItemMobile: {
		width: '150px',
		color: '#fff',
	},
	listItemFocus: {
		outline: '1px dash white',
	},
	listItemIcon: {
		fontSize: '1rem',
	},
	primaryListText: {
		fontSize: '0.75rem',
		lineHeight: 1.5,
	},
	focusButton: {
		'&:focus': {
			color: '#ffffff',
		},
	},
}));

export const LanguageSettingMenu: React.FC<QualitySettingMenuProps> = ({
	playerConfigs,
	sideWindowOpen,
	className,
	isMobile,
	queryParams,
	onLanguageChange,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const firstTime = useRef<number>(0);

	const { openSettingMenu, mediasData, currentMediaId, playlistId, transcriptions, subtitleLanguage, showTranscriptionsModule } = useSelector((state) => (state as any).configurationReducer);
	const { playerReady, isPlaying } = useSelector((state) => (state as any).playerReducer);

	const [selectedSubtitle, setSelectedSubtitle] = useState<{
		code: string;
		impaired?: boolean;
	} | null>({ code: 'none' });
	const [selectedLanguage, setSelectedLanguage] = useState<{
		code: string;
		impaired?: boolean;
	} | null>({ code: 'default' });
	const [hoveringItem, setHoveringItem] = useState<{
		code: string;
		impaired?: boolean;
	} | null>(null);
	const [selectedMenu, setSelectedMenu] = useState('');
	const [selectedMenuHoveringItem, setSelectedMenuHoveringItem] = useState('');

	const playerId = useMemo(() => `player-${playlistId || currentMediaId}`, [playlistId, currentMediaId]);
	const currentMedia = useMemo(
		() => mediasData.find(({ id, originalId }: any) => id === currentMediaId || originalId === currentMediaId),
		[mediasData, currentMediaId]
	);
	const classes = useListCustomStyle();

	const subtitleItems = useMemo(() => {
		if (!currentMedia || (currentMedia.subtitles ?? []).length === 0) {
			return [];
		}
		const result: any[] = [];
		currentMedia.subtitles.forEach((s: any) => {
			if (!result.find((r) => r.language.code === s.language.code)) {
				result.push(s);
			}
		});

		return result;
	}, [currentMedia]);

	const languageItems = useMemo(() => {
		if (!currentMedia || (currentMedia.audioTracks ?? []).length === 0) {
			return [];
		}

		const result: any[] = [];
		currentMedia.audioTracks.forEach((s: any) => {
			if (!result.find((r) => r.language.code === s.language.code && r.language.impaired === s.language.impaired)) {
				result.push(s);
			}
		});

		return result;
	}, [currentMedia]);

	const changeSubtitle = useCallback(
		(item: any) => {
			if (item?.code === selectedSubtitle?.code) {
				return;
			}
			const sub = item === 'none' ? { code: 'none' } : item;
			setSelectedSubtitle(sub);
			dispatch(setSubtitleLanguage(sub));

			if (item === 'none' || sub?.code === 'none') {
				(window as any).GoBrain.widgets(playerId)?.subtitles?.(null);
			} else {
				(window as any).GoBrain.widgets(playerId)?.subtitles?.(sub.code);
			}
			onLanguageChange?.('subtitle', sub);
		},
		[selectedSubtitle?.code, dispatch, onLanguageChange, playerId]
	);

	const changeLanguage = useCallback(
		(item: any) => {
			if (item?.code === selectedLanguage?.code && item?.impaired === selectedLanguage?.impaired) {
				return;
			}

			setSelectedLanguage(item);
			(window as any).GoBrain.widgets(playerId).audioTrack(item);
		},
		[playerId, selectedLanguage]
	);

	const onMenuItemClick = useCallback(
		(_e: React.MouseEvent, item: any, type = 'language') => {
			if (type === 'language') {
				changeLanguage(item);
			} else {
				changeSubtitle(item);
			}
		},
		[changeSubtitle, changeLanguage]
	);

	const onCloseMenu = useCallback(() => {
		dispatch(setOpenSettingMenu(null));
		setHoveringItem(null);
		setSelectedMenu('');
	}, [dispatch]);

	const onKeyDown = useCallback(
		(event, item: any, type = 'language') => {
			if (event.key === 'Tab') {
				event.stopPropagation();
				return;
			}

			if (event.key === 'Enter' || event.key === ' ') {
				if (type === 'language') {
					changeLanguage(item);
				} else {
					changeSubtitle(item);
				}
				event.stopPropagation();
			}
		},
		[changeSubtitle, changeLanguage]
	);

	const openTranscriptionModule = useCallback(() => {
		dispatch(setShowTranscriptionsModule(!showTranscriptionsModule));
		dispatch(setShowChatWindow(false));
		dispatch(setShowShoppingCart(false));
		onCloseMenu();
	}, [dispatch, onCloseMenu, showTranscriptionsModule]);

	const onTranscriptMenuKeyDown = useCallback((event: any) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') {
			event.stopPropagation();
			openTranscriptionModule();
		}
	}, [openTranscriptionModule]);

	const handleKeydownLanguageSetting = (event: any, type: any) => {
		if (event.key === 'Tab') {
			event.stopPropagation();
			return;
		}
		if (event.key === 'Enter' || event.key === ' ') {
			setSelectedMenu(type);
		}
	};

	useEffect(() => {
		if (!playerReady || isPlaying === undefined || firstTime.current >= 2) {
			return;
		}

		if (firstTime.current < 1) {
			if (playerConfigs?.playbackOptions?.subtitlesAutoOn) {
				const { language: paramLanguage } = queryParams ?? {};
				const language = paramLanguage ? paramLanguage : playerConfigs?.accessibilitySettings?.language;
				changeSubtitle({ code: language === 'dk' ? 'da' : language });
			} else {
				if (playlistId) {
					return;
				}
				const widget = (window as any).GoBrain.widgets(playerId);
				widget &&
					widget.subtitles?.().then((code: any) => {
						setSelectedSubtitle({ code: code ?? 'none' });
					});
			}
		}

		if (firstTime.current < 2) {
			if (isSafari || isIOS) {
				setSelectedLanguage({ code: 'default' });
			}
		}

		firstTime.current++;
	}, [
		playerId,
		playerReady,
		queryParams,
		playerConfigs?.accessibilitySettings?.language,
		playerConfigs?.playbackOptions?.subtitlesAutoOn,
		changeSubtitle,
		playlistId,
		isPlaying,
	]);

	useEffect(() => {
		if (openSettingMenu?.type === 'language') {
			return;
		}
		return () => {
			setHoveringItem(null);
			setSelectedMenu('');
		};
	}, [openSettingMenu]);

	useEffect(() => {
		if (subtitleLanguage?.code && subtitleLanguage?.code !== selectedSubtitle?.code) {
			const subtitleCorrespondingItem = subtitleItems.find((item: any) => item?.language?.code === subtitleLanguage?.code)
											?? subtitleItems.find((item: any) => item?.language?.code?.includes(subtitleLanguage?.code));
			subtitleCorrespondingItem?.language && changeSubtitle(subtitleCorrespondingItem?.language);
		}
	}, [changeSubtitle, selectedSubtitle, subtitleItems, subtitleLanguage]);

	const renderSettingMenuItems = () => {
		const listItemClass = classes.listItem;
		const showAllMenus = [languageItems, subtitleItems, transcriptions?.entries].filter(Boolean).filter((item) => item.length > 0).length > 1;

		return (
			<>
				{!selectedMenu && showAllMenus && (
					<>
						{isMobile && (
							<>
								<List>
									<ListItem tabIndex={0} className={listItemClass}>
										<ListItemText classes={{ primary: classes.primaryListText }} primary={t('AUDIO_SUBTITLES')} />
										<IconButton
											aria-label={t('LABEL_CLOSE_MEDIA_LIST')}
											style={{ minWidth: '30px', marginLeft: 'auto', height: '32px', width: '32px' }}
											onClick={onCloseMenu}
											className={classes.focusButton}
										>
											<Close className={classes.listItemIcon} htmlColor="#fff" />
										</IconButton>
									</ListItem>
								</List>

								<Divider
									tabIndex={-1}
									aria-hidden="true"
									style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', margin: '0 5px 3px' }}
								/>
							</>
						)}
						<List className={isMobile ? 'qplayer__setting-menu--mobile-scrollable' : ''} id="sub-header">
							{languageItems.length > 0 && (
								<MenuUtils.MenuSelector
									menuType="language"
									itemLabel={t('AUDIO_LANGUAGE')}
									isMobile={isMobile}
									hoveringItem={selectedMenuHoveringItem}
									playerConfigs={playerConfigs}
									onSelectMenu={setSelectedMenu}
									onKeyDown={handleKeydownLanguageSetting}
									onCloseMenu={onCloseMenu}
									onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
									onLeaveItem={() => setSelectedMenuHoveringItem("")}
								/>
							)}
							{subtitleItems.length > 0 && (
								<MenuUtils.MenuSelector
									menuType="subtitle"
									itemLabel={t('SUBTITLES')}
									isMobile={isMobile}
									hoveringItem={selectedMenuHoveringItem}
									playerConfigs={playerConfigs}
									onSelectMenu={setSelectedMenu}
									onKeyDown={handleKeydownLanguageSetting}
									onCloseMenu={onCloseMenu}
									onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
									onLeaveItem={() => setSelectedMenuHoveringItem("")}
								/>
							)}
							{(transcriptions?.entries ?? []).length > 0 && (
								<MenuUtils.MenuSelector
									menuType="transcription"
									itemLabel={t('TRANSCRIPTION')}
									isMobile={isMobile}
									hoveringItem={selectedMenuHoveringItem}
									playerConfigs={playerConfigs}
									onSelectMenu={openTranscriptionModule}
									onKeyDown={onTranscriptMenuKeyDown}
									onCloseMenu={onCloseMenu}
									onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
									onLeaveItem={() => setSelectedMenuHoveringItem("")}
								/>
							)}
						</List>
					</>
				)}

				{languageItems.length > 0 && (selectedMenu === 'language' || (subtitleItems.length === 0 && (transcriptions?.entries ?? []).length === 0)) && (
					<MenuUtils.MenuPanel
						menuType="language"
						items={languageItems}
						isMobile={isMobile}
						hoveringItem={hoveringItem}
						selectedItem={selectedLanguage}
						playerConfigs={playerConfigs}
						showAllMenus={showAllMenus}
						selectedMenu={selectedMenu}
						onSelectMenu={setSelectedMenu}
						onMenuItemClick={onMenuItemClick}
						onKeyDown={onKeyDown}
						onCloseMenu={onCloseMenu}
						onHoverItem={(lang: any) => setHoveringItem(lang)}
						onLeaveItem={() => setHoveringItem(null)}
					/>
				)}

				{subtitleItems.length > 0 && (selectedMenu === 'subtitle' || (languageItems.length === 0 && (transcriptions?.entries ?? []).length === 0)) && (
					<MenuUtils.MenuPanel
						menuType="subtitle"
						items={subtitleItems}
						isMobile={isMobile}
						hoveringItem={hoveringItem}
						selectedItem={selectedSubtitle}
						playerConfigs={playerConfigs}
						showAllMenus={showAllMenus}
						selectedMenu={selectedMenu}
						onSelectMenu={setSelectedMenu}
						onMenuItemClick={onMenuItemClick}
						onKeyDown={onKeyDown}
						onCloseMenu={onCloseMenu}
						onHoverItem={(lang: any) => setHoveringItem(lang)}
						onLeaveItem={() => setHoveringItem(null)}
					/>
				)}

				{(transcriptions?.entries ?? []).length > 0 && (selectedMenu === 'transcription' || (languageItems.length === 0 && subtitleItems.length === 0)) && (
					<MenuUtils.MenuSelector
						menuType="transcription"
						itemLabel={t('TRANSCRIPTION')}
						isMobile={isMobile}
						hoveringItem={selectedMenuHoveringItem}
						playerConfigs={playerConfigs}
						onSelectMenu={openTranscriptionModule}
						onKeyDown={onTranscriptMenuKeyDown}
						onCloseMenu={onCloseMenu}
						onHoverItem={(menu: string) => setSelectedMenuHoveringItem(menu)}
						onLeaveItem={() => setSelectedMenuHoveringItem("")}
					/>
				)}

			</>
		);
	};

	if (isMobile) {
		return (
			<Dialog
				className="qplayer__setting-menu"
				open={openSettingMenu?.type === 'language'}
				style={{ zIndex: '99000', width: sideWindowOpen ? '75%' : '100%', backgroundColor: '#000000' }}
				onClose={onCloseMenu}
				fullWidth
				maxWidth="lg"
			>
				<div className="qplayer__setting-menu--mobile" onMouseDown={(e) => e.stopPropagation()}>
					{renderSettingMenuItems()}
				</div>
			</Dialog>
		);
	}

	return openSettingMenu?.type === 'language' ? (
		<Menu
			onMouseDown={(e) => e.stopPropagation()}
			style={{ zIndex: '99000', position: 'static' }}
			key={'quality-menu'}
			className={joinClassNames('qplayer__setting-menu', className)}
			open={openSettingMenu?.type === 'language'}
			onClose={onCloseMenu}
			getContentAnchorEl={null}
			anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
			transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
			anchorEl={openSettingMenu?.anchor || document.querySelector('.bottomControlbar')}
			PaperProps={{
				style: openSettingMenu?.anchor ? { transform: 'translateY(-30px)' } : {},
				className: 'closeable-by-tab',
			}}
			MenuListProps={{
				'aria-labelledby': 'sub-header',
			}}
		>
			<div tabIndex={-1}>{renderSettingMenuItems()}</div>
		</Menu>
	) : null;
};
